import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditWebApiHandler_hasCarpetFloorCovering,
  useRentalManagerUserPropertyEditWebApiHandler_hasConcreteFloorCovering,
  useRentalManagerUserPropertyEditWebApiHandler_hasHardwoodFloorCovering,
  useRentalManagerUserPropertyEditWebApiHandler_hasLaminateFloorCovering,
  useRentalManagerUserPropertyEditWebApiHandler_hasLinoleumFloorCovering,
  useRentalManagerUserPropertyEditWebApiHandler_hasSlateFloorCovering,
  useRentalManagerUserPropertyEditWebApiHandler_hasSoftwoodFloorCovering,
  useRentalManagerUserPropertyEditWebApiHandler_hasTileFloorCovering,
  useRentalManagerUserPropertyEditWebApiHandler_hasVinylFloorCovering,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { getIsInputFormDisabled } from '../../utils';

import { DEFAULT_ERROR_MESSAGE } from '../../../../constants/ErrorMessageConstants';
import {
  FLOOR_COVERING_KEYS,
  FLOOR_COVERING_METADATA,
  type FloorCoveringKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<FloorCoveringKey, boolean>;

const FloorsAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const floorCoveringTypes = listingDetails?.buildingAttributes?.floorCoveringTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasHardwood: floorCoveringTypes?.hasHardwood ?? false,
    hasTile: floorCoveringTypes?.hasTile ?? false,
    hasCarpet: floorCoveringTypes?.hasCarpet ?? false,
    hasLaminate: floorCoveringTypes?.hasLaminate ?? false,
    hasLinoleum: floorCoveringTypes?.hasLinoleum ?? false,
    hasVinyl: floorCoveringTypes?.hasVinyl ?? false,
    hasSlate: floorCoveringTypes?.hasSlate ?? false,
    hasSoftwood: floorCoveringTypes?.hasSoftwood ?? false,
    hasConcrete: floorCoveringTypes?.hasConcrete ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Floor covering updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateHardwood } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasHardwoodFloorCovering,
    skipToast: true,
    meta: { context: { api: 'hasHardwoodFloorCovering', details: { listingAlias } } },
  });

  const { mutateAsync: updateTile } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasTileFloorCovering,
    skipToast: true,
    meta: { context: { api: 'hasTileFloorCovering', details: { listingAlias } } },
  });

  const { mutateAsync: updateCarpet } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasCarpetFloorCovering,
    skipToast: true,
    meta: { context: { api: 'hasCarpetFloorCovering', details: { listingAlias } } },
  });

  const { mutateAsync: updateLaminate } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasLaminateFloorCovering,
    skipToast: true,
    meta: { context: { api: 'hasLaminateFloorCovering', details: { listingAlias } } },
  });

  const { mutateAsync: updateLinoleum } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasLinoleumFloorCovering,
    skipToast: true,
    meta: { context: { api: 'hasLinoleumFloorCovering', details: { listingAlias } } },
  });

  const { mutateAsync: updateVinyl } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasVinylFloorCovering,
    skipToast: true,
    meta: { context: { api: 'hasVinylFloorCovering', details: { listingAlias } } },
  });

  const { mutateAsync: updateSlate } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasSlateFloorCovering,
    skipToast: true,
    meta: { context: { api: 'hasSlateFloorCovering', details: { listingAlias } } },
  });

  const { mutateAsync: updateSoftwood } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasSoftwoodFloorCovering,
    skipToast: true,
    meta: { context: { api: 'hasSoftwoodFloorCovering', details: { listingAlias } } },
  });

  const { mutateAsync: updateConcrete } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditWebApiHandler_hasConcreteFloorCovering,
    skipToast: true,
    meta: { context: { api: 'hasConcreteFloorCovering', details: { listingAlias } } },
  });

  const mutationMap: Record<keyof LocalState, typeof updateHardwood> = {
    hasHardwood: updateHardwood,
    hasTile: updateTile,
    hasCarpet: updateCarpet,
    hasLaminate: updateLaminate,
    hasLinoleum: updateLinoleum,
    hasVinyl: updateVinyl,
    hasSlate: updateSlate,
    hasSoftwood: updateSoftwood,
    hasConcrete: updateConcrete,
  };

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as FloorCoveringKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = FLOOR_COVERING_METADATA[fieldName];
      await mutationMap[fieldName]({
        getParams: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(err, DEFAULT_ERROR_MESSAGE);
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Floors</Legend>} disabled={isInputFormDisabled}>
      {FLOOR_COVERING_KEYS.map((fieldName) => {
        const metadata = FLOOR_COVERING_METADATA[fieldName];
        return (
          <LabeledControl
            key={`floorCoveringTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(FloorsAmenities);
